import { useState } from 'react'
import Badge from 'core/elements/badge'
import type { StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import type { ClusterAddonStatus } from 'k8s/components/kaapi/cluster-addons/etcd-backup'
import AddonErrorModal from './AddonErrorModal'

export const Phase = {
  Provisioning: 'Provisioning',
  Provisioned: 'Provisioned',
  FailedNonRetriable: 'FailedNonRetriable',
  Failed: 'Failed',
  Removing: 'Removing',
  Removed: 'Removed',
  Unknown: 'Unknown',
  NotEnabled: 'NotEnabled',
}

export const getPhaseStatus = (phase: string): StatusCellModel => {
  switch (phase) {
    case Phase.Provisioned:
      return { variant: 'success', label: phase }
    case Phase.Provisioning:
      return { variant: 'warning', label: phase }
    case Phase.Failed:
    case Phase.FailedNonRetriable:
    case Phase.Removing:
    case Phase.Removed:
      return { variant: 'error', label: phase }
    case Phase.NotEnabled:
      return { variant: 'unknown', label: 'Not enabled' }
    default:
      return { variant: 'unknown', label: Phase.Unknown }
  }
}

const FAILED_PHASES = [Phase.Failed, Phase.FailedNonRetriable]
const isFailed = (status?: ClusterAddonStatus) => FAILED_PHASES.includes(status?.status)

// FR-009: a failed add-on with a message opens its error details; any other add-on's
// tooltip is its failure message, such as why it is still provisioning
export const getStatusTooltip = (status?: ClusterAddonStatus): string | undefined =>
  isFailed(status) && status?.failureMessage
    ? 'Click to view error details'
    : status?.failureMessage || undefined

interface AddonStatusBadgeProps {
  addonName: string
  status?: ClusterAddonStatus
}

// FR-009: the status badge; a failed one opens AddonErrorModal
export function AddonStatusBadge({ addonName, status }: AddonStatusBadgeProps) {
  const [showError, setShowError] = useState(false)
  const { variant, label } = getPhaseStatus(status?.status || Phase.Provisioning)
  const clickable = isFailed(status) && !!status?.failureMessage
  return (
    <>
      <Badge
        variant={variant}
        text={label}
        ellipsisAt={30}
        tooltipBody={getStatusTooltip(status)}
        onClick={
          clickable
            ? (e) => {
                e.stopPropagation()
                setShowError(true)
              }
            : undefined
        }
      />
      {showError && (
        <AddonErrorModal
          addonName={addonName}
          reason={status?.failureReason}
          message={status?.failureMessage as string}
          onClose={() => setShowError(false)}
        />
      )}
    </>
  )
}
